<script lang="ts" setup>
import type { LogicalFilterAND } from '#layers/data-model';
import type { FilterSchemaCollection } from './utils/filter-schema';
import { DisplayTemplateConfigurationError, isEmptyFilter, isFieldFilter, isLogicalFilter } from '#layers/data-model';
import { useLogicalFilter } from '~/composables/use-logical-filter';
import { injectFilterSchemaContext } from './utils/filter-schema-context';
import { useFilterConfigResolver } from './utils/use-filter-config-resolver';

defineProps<{
	collection: FilterSchemaCollection;
}>();

const filter = defineModel<LogicalFilterAND>({ default: () => ({ _and: [] }) });

const { findPrimitiveFieldById, findSingleRelationFieldById } = injectFilterSchemaContext();
const { supportedOperatorsFor } = useFilterConfigResolver();

const { handleRemove, handleAdd, handleUpdate, handleGroupAdd } = useLogicalFilter(filter, {
	addGroupToFront: true,
	findPrimitiveFieldById,
	findSingleRelationFieldById,
	supportedOperatorsFor,
});
</script>

<template>
	<NuxtErrorBoundary>
		<template #error>
			<span class="flex items-center gap-2">
				<DisplayTemplateConfigurationError :error="{ message: $t('filters.invalid_filter') }" />
				<DButton variant="ghost" color="neutral" size="xs" @click="filter = { _and: [] }">
					{{ $t('filters.clear_filter') }}
				</DButton>
			</span>
		</template>

		<div class="flex flex-1 shrink-0 items-center gap-2 overflow-hidden" :class="{ '-ms-4.5': isEmptyFilter(filter) }">
			<div class="scrollbar-hidden flex items-center gap-2 overflow-x-auto">
				<template
					v-for="(subFilter, index) in filter._and"
					:key="index"
				>
					<FilterAdvanced
						v-if="isLogicalFilter(subFilter)"
						:model-value="subFilter"
						:collection
						@update:model-value="handleUpdate(index, $event)"
						@remove="handleRemove(index)"
					/>
					<FilterSimple
						v-else-if="isFieldFilter(subFilter)"
						:model-value="subFilter"
						:collection
						@update:model-value="handleUpdate(index, $event)"
						@remove="handleRemove(index)"
					/>
				</template>
			</div>
			<FilterAdd
				:collection
				@add-filter="handleAdd"
				@add-group="handleGroupAdd"
			/>
		</div>
	</NuxtErrorBoundary>
</template>
